<template>
  <div class="w-full" :key="order.updated_at">
    <portal to="search">
      <div class="flex items-center">
      <h1 class="font-bold text-lg md:text-2xl">
        <inertia-link
          class="text-indigo-400 hover:text-indigo-600"
          :href="route('customer.orders')"
        >
          Customer Orders
        </inertia-link>
        <span class="text-indigo-400 font-medium">/</span>
        Order #{{ order.id }}
      </h1>
      </div>
    </portal>

    <div class="grid grid-cols-1 sm:grid-cols-12">
      <div class="sm:col-span-12">
        <div>
          <OrderTabs
            active="delivery"
            :order-id="order.id"
            />
          <OrderTabContent>
            <div class="grid grid-cols-1 xl:grid-cols-12 order-last xl:order-first">
              <div class="px-6 col-span-full xl:col-span-3">
                <div class="mt-4 pb-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 class="text-lg leading-6 font-medium text-gray-900">
                    Handover/Delivery Details
                  </h3>
                  <UpdateDeliveryDetailsModal
                    v-if="canAdmin('update-orders') && isEditable"
                    v-slot="{open}"
                    :order="order"
                    :is-locked="isLocked"
                    :vesselLocations="vesselLocations"
                    :locations="locations"
                  >
                    <button class="btn-primary" @click="open" :disabled="isLocked">Edit</button>
                  </UpdateDeliveryDetailsModal>
                </div>
                <dl class="my-5 divide-y divide-gray-100 border py-5 px-3">
                  <div class="px-4 py-4 flex flex-col space-y-3">
                    <dt class="text-sm/6 font-light text-gray-900">Customer Contact Number</dt>
                    <dd class="mt-1 text-sm/6 font-medium text-gray-700 sm:col-span-2 sm:mt-0">{{ order.customer_contact_number }}</dd>
                  </div>
                  <div class="px-4 py-4 flex flex-col space-y-3">
                    <dt class="text-sm/6 font-light text-gray-900">Delivery Method</dt>
                    <dd class="mt-1 text-sm/6 font-medium text-gray-700 sm:col-span-2 sm:mt-0">{{ order.customer_to_collect ? 'Collection by Customer' : 'To be Delivered' }}</dd>
                  </div>
                  <template v-if="!order.customer_to_collect">

                    <div class="px-4 py-4 flex flex-col space-y-3">
                      <dt class="text-sm/6 font-light text-gray-900">Location</dt>
                      <dd class="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">{{ order.delivery_location }}</dd>
                    </div>
                    <div class="px-4 py-4 flex flex-col space-y-3">
                      <dt class="text-sm/6 font-light text-gray-900">Delivery Address</dt>
                      <dd class="mt-1 text-sm/6 font-medium text-gray-700 sm:col-span-2 sm:mt-0">{{ order.delivery_address || 'N/A' }}</dd>
                    </div>
                    <div class="px-4 py-4 flex flex-col space-y-3">
                      <dt class="text-sm/6 font-light text-gray-900">Vessel Name</dt>
                      <dd class="mt-1 text-sm/6 font-medium text-gray-700 sm:col-span-2 sm:mt-0">{{ order.vessel || 'N/A' }}</dd>
                    </div>
                    <div class="px-4 py-4 flex flex-col space-y-3">
                      <dt class="text-sm/6 font-light text-gray-900">Vessel Location</dt>
                      <dd class="mt-1 text-sm/6 font-medium text-gray-700 sm:col-span-2 sm:mt-0">{{ order.vessel_location || 'N/A' }}</dd>
                    </div>
                  </template>
                  <div class="px-4 py-4 flex flex-col space-y-3">
                    <dt class="text-sm/6 font-light text-gray-900">Delivery/Handover Date</dt>
                    <dd class="mt-1 text-sm/6 font-medium text-gray-700 sm:col-span-2 sm:mt-0">{{ order.customer_handover_date | localDate }}</dd>
                  </div>
                  <div class="px-4 py-4 flex flex-col space-y-3">
                    <dt class="text-sm/6 font-light text-gray-900">Delivery Contact Number</dt>
                    <dd class="mt-1 text-sm/6 font-medium text-gray-700 sm:col-span-2 sm:mt-0">{{ order.delivery_contact_number }}</dd>
                  </div>
                </dl>
              </div>
              <div class="px-6 col-span-full xl:col-span-9 order-first xl:order-last">
                <div class="mt-4 pb-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 class="text-lg leading-6 font-medium text-gray-900">
                    Dispatch Notes
                  </h3>
                  <CreateDeliveryNoteModal
                    v-if="canAdmin('access-dispatch-notes') && canAdmin('create-dispatch-notes') && (order.status === 'invoiced' || order.status === 'delivery_in_progress')"
                    v-slot="{open}"
                    :order="order"
                    :warehouse-list="warehouseList"
                    :getWarehouseListFor="getWarehouseListFor"
                    :vessel-locations="vesselLocations"
                    :locations="locations"
                    key="create-deliery-note"
                  >
                    <button
                      :disabled="!canAdmin('create-dispatch-notes') && (!order.customer_handover_date || isLocked)"
                      @click="open"
                      class="btn-primary"
                    >
                      New Note
                    </button>
                  </CreateDeliveryNoteModal>
                </div>
                <DeliveryNotes
                  v-if="canAdmin('access-dispatch-notes')"
                  :delivery-notes="dispatchNotes"
                  :warehouse-list="warehouseList"
                  :locations="locations"
                  :vessel-locations="vesselLocations"
                  :order="order"
                  :getWarehouseListFor="getWarehouseListFor"
                  :vessels="vessels"
                  :is-locked="isLocked"
                />
                <div v-else class="text-center py-10">You do not have permission to view dispatch notes.</div>
              </div>
            </div>
          </OrderTabContent>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from "../../../Shared/Admin/Layout.vue";
import OrderTabs from "./Partials/OrderTabs.vue";
import OrderTabContent from "./Partials/OrderTabContent.vue";
import DeliveryToBoat from "./DeliveryToBoat.vue";
import TextareaInput from "../../../Shared/TextareaInput.vue";
import Icon from "../../../Shared/Icon.vue";
import TextInput from "../../../Shared/TextInput.vue";
import DateInput from "../../../Shared/DateInput.vue";
import SelectInput from "../../../Shared/SelectInput.vue";
import LoadingButton from "../../../Shared/LoadingButton.vue";
import DeliveryNotes from "./Partials/DeliveryNotes/DeliveryNotes.vue";
import CreateDeliveryNoteModal from "./Partials/DeliveryNotes/CreateDeliveryNoteModal.vue";
import UpdateDeliveryDetailsModal from "./Partials/UpdateDeliveryDetailsModal.vue";

export default {
  name: 'DeliveryDetails',
  components: {
    UpdateDeliveryDetailsModal,
    CreateDeliveryNoteModal, DeliveryNotes, LoadingButton, SelectInput, DateInput, TextInput, Icon, TextareaInput, DeliveryToBoat, OrderTabContent, OrderTabs},
  layout: Layout,
  metaInfo: { title: 'Order Delivery Details' },

  props: {
    order: Object,
    isLocked: Boolean,
    locations: Object,
    warehouseList: Array,
    warehouses: Object,
    dispatchNotes: Array,
    vesselLocations: Object,
    vessels: Array,
  },

  data() {
    return {
      form: this.$inertia.form({
        customer_to_collect: false,
        delivery_location: null,
        delivery_address: null,
        delivery_contact_number: null,
        customer_handover_date: null,
        customer_contact_number: null,
        boat_id: null,
        vessel: null,
        vessel_location: null,
      })
    }
  },

  computed: {
    isEditable() {
      const notes = this.dispatchNotes.filter(itm => itm.status !== 'void')
      return notes.length === 0
    },
  },

  methods: {
    updateOrder() {
      if (this.canAdmin('update-orders') || this.canAdmin('update-order-item-location')) {
        this.form.put(this.route('customer.orders.fulfilment-details.update', this.order.id))
      }
    },

    getWarehouseListFor(item_code) {
      let items = [];

      if(this.warehouses[item_code]) {

        this.warehouses[item_code].forEach((item) => {
          items.push({
            id: item.id,
            name: item.name
          })
        });
      }

      return items;
    },

    updateSelectedBoatID(boatSelected) {
      this.form.boat_id = boatSelected.id;
      this.form.vessel = boatSelected.name;
    }
  },
}
</script>
